import { useEffect, useState } from "react";
import { api, TODO_PRIORITY, TODO_PRIORITY_MAP, formatApiError } from "@/lib/api";
import { formatDate } from "@/lib/date";
import DateInput from "@/components/DateInput";
import { CheckCircle2, Circle, Trash2, ListChecks, Plus } from "lucide-react";
import { toast } from "sonner";

export default function Todos() {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("open"); // open | done | all
  const [form, setForm] = useState({ title: "", due_date: "", priority: "normal", note: "" });
  const [loading, setLoading] = useState(false);

  async function load() {
    try {
      const { data } = await api.get("/todos");
      setItems(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  }
  useEffect(() => { load(); }, []);

  async function add(e) {
    e.preventDefault();
    if (!form.title.trim()) return;
    setLoading(true);
    try {
      await api.post("/todos", {
        title: form.title.trim(),
        due_date: form.due_date || null,
        priority: form.priority || "normal",
        note: form.note || "",
      });
      setForm({ title: "", due_date: "", priority: "normal", note: "" });
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setLoading(false); }
  }

  async function toggle(id) {
    try {
      await api.patch(`/todos/${id}/toggle`);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  }

  async function remove(id) {
    if (!window.confirm("Ștergi acest task?")) return;
    await api.delete(`/todos/${id}`);
    load();
  }

  const visible = items.filter((t) => {
    if (filter === "open") return !t.done;
    if (filter === "done") return t.done;
    return true;
  });

  const openCount = items.filter((t) => !t.done).length;
  const doneCount = items.length - openCount;

  function isOverdue(t) {
    if (!t.due_date || t.done) return false;
    const d = new Date(t.due_date);
    const today = new Date(); today.setHours(0,0,0,0);
    return d < today;
  }

  return (
    <div className="sm:pl-52 space-y-4" data-testid="todos-page">
      <div className="flex items-end justify-between">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-slate-400">Checklist</div>
          <h1 className="font-display text-3xl tracking-tight font-medium">Task-uri</h1>
        </div>
        <span className="text-sm text-slate-500 tabular">{openCount} de făcut · {doneCount} gata</span>
      </div>

      {/* Add form */}
      <form onSubmit={add} className="bg-white border border-slate-200 rounded-2xl p-3 space-y-2" data-testid="todo-form">
        <div className="grid grid-cols-12 gap-2">
          <input
            data-testid="todo-title"
            placeholder="Ce ai de făcut?"
            value={form.title}
            onChange={(e) => setForm({ ...form, title: e.target.value })}
            className="col-span-12 sm:col-span-6 border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#0047FF]"
          />
          <div className="col-span-6 sm:col-span-3">
            <DateInput testid="todo-due" value={form.due_date} onChange={(v) => setForm({ ...form, due_date: v })} />
          </div>
          <select
            data-testid="todo-priority"
            value={form.priority}
            onChange={(e) => setForm({ ...form, priority: e.target.value })}
            className="col-span-4 sm:col-span-2 border border-slate-200 rounded-lg px-2 py-2 text-sm bg-white"
          >
            {TODO_PRIORITY.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
          </select>
          <button
            data-testid="todo-add-btn"
            type="submit"
            disabled={loading || !form.title.trim()}
            className="col-span-2 sm:col-span-1 bg-[#0047FF] hover:bg-[#003BD3] disabled:opacity-50 text-white rounded-lg text-sm py-2 inline-flex items-center justify-center"
          >
            <Plus className="w-4 h-4" />
          </button>
        </div>
        <input
          data-testid="todo-note"
          placeholder="Notă (opțional)"
          value={form.note}
          onChange={(e) => setForm({ ...form, note: e.target.value })}
          className="w-full border border-slate-200 rounded-lg px-3 py-1.5 text-sm text-slate-600"
        />
      </form>

      {/* Filter tabs */}
      <div className="flex gap-2">
        {[
          { k: "open", label: `De făcut (${openCount})` },
          { k: "done", label: `Finalizate (${doneCount})` },
          { k: "all", label: `Toate (${items.length})` },
        ].map((t) => (
          <button
            key={t.k}
            data-testid={`todo-filter-${t.k}`}
            onClick={() => setFilter(t.k)}
            className={`text-xs rounded-full px-3 py-1.5 border transition-colors ${
              filter === t.k ? "bg-[#0A0F1C] text-white border-[#0A0F1C]" : "bg-white text-slate-600 border-slate-200 hover:border-slate-400"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* List */}
      {visible.length === 0 ? (
        <div className="bg-white border border-slate-200 rounded-2xl p-10 text-center">
          <ListChecks className="w-10 h-10 mx-auto text-slate-300" />
          <div className="mt-3 font-display text-lg">
            {filter === "done" ? "Niciun task finalizat încă" : filter === "open" ? "Nimic de făcut 🎉" : "Niciun task"}
          </div>
          <div className="text-sm text-slate-500 mt-1">
            {filter === "open" && "Adaugă primul task mai sus — bate-l pe listă ca să nu uiți."}
          </div>
        </div>
      ) : (
        <div className="bg-white border border-slate-200 rounded-2xl divide-y divide-slate-100">
          {visible.map((t) => {
            const p = TODO_PRIORITY_MAP[t.priority] || TODO_PRIORITY_MAP.normal;
            const overdue = isOverdue(t);
            return (
              <div key={t.id} className="flex items-start gap-3 px-4 py-3 hover:bg-slate-50" data-testid={`todo-row-${t.id}`}>
                <button
                  data-testid={`todo-toggle-${t.id}`}
                  onClick={() => toggle(t.id)}
                  className={`shrink-0 mt-0.5 ${t.done ? "text-emerald-600" : "text-slate-300 hover:text-[#0047FF]"}`}
                  aria-label={t.done ? "Marchează ca nefinalizat" : "Marchează ca finalizat"}
                >
                  {t.done ? <CheckCircle2 className="w-5 h-5" /> : <Circle className="w-5 h-5" />}
                </button>
                <div className="min-w-0 flex-1">
                  <div className={`text-sm font-medium flex items-center gap-2 flex-wrap ${t.done ? "line-through text-slate-400" : ""}`}>
                    <span className="break-words">{t.title}</span>
                    <span
                      className="text-[10px] uppercase tracking-widest font-medium px-1.5 py-0.5 rounded-full"
                      style={{ backgroundColor: p.color + "22", color: p.color }}
                    >
                      {p.label}
                    </span>
                    {overdue && (
                      <span className="text-[10px] uppercase tracking-widest font-medium px-1.5 py-0.5 rounded-full bg-rose-50 text-rose-700">Întârziat</span>
                    )}
                  </div>
                  {(t.due_date || t.note) && (
                    <div className="text-xs text-slate-500 mt-0.5">
                      {t.due_date && <span>📅 {formatDate(t.due_date)}</span>}
                      {t.due_date && t.note && " · "}
                      {t.note && <span>{t.note}</span>}
                    </div>
                  )}
                </div>
                <button
                  data-testid={`todo-delete-${t.id}`}
                  onClick={() => remove(t.id)}
                  className="shrink-0 text-slate-300 hover:text-red-600"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
